<template>
  <div>
    <div class="is-flex is-align-items-center is-justify-content-space-between">
      <button
        v-if="[singleProcess.isOpen, bulkProcess.isOpen].includes(true)"
        class="button is-button-secondary is-rounded mr-5"
        @click="showTransactionsValidation">
        {{ $t('generalWords.GoBack') }}
      </button>
    </div>
    <div id="debt-selection-card" class="card-item mt-6 select-content">
      <section>
        <div class="spinner-content is-flex is-align-content-center" :class="isLoading ? 'is-loading' : 'is-hidden'">
          <div class="spinner">
            <div class="double-bounce1" />
            <div class="double-bounce2" />
          </div>
        </div>
        <div v-if="!isLoading" class="fees-content">
          <div v-if="!singleProcess.isOpen && !bulkProcess.isOpen">
            <div id="validation-buttons" class="field is-grouped">
              <p v-if="confirmableTransactionsLen" class="control">
                <button
                  id="filter-button"
                  class="button is-button-secondary is-rounded"
                  @click="filters.isModalVisible = true">
                  <span class="icon is-small">
                    <i class="fas fa-filter" />
                  </span>
                  <span>{{ $t('generalWords.Filter') }}</span>
                </button>
              </p>
              <p v-if="$i18n.locale==='cl' && confirmableTransactionsLen" class="control">
                <button
                  id="bulk-button"
                  :disabled="!checkUserFeature('element.transaction-approvals.button.lotes')"
                  class="button is-button-secondary is-rounded"
                  @click="openBulkProcess">
                  <span class="icon is-small">
                    <i class="fas fa-layer-group" />
                  </span>
                  <span>{{ $t('generalWords.Bulk') }}</span>
                </button>
              </p>
            </div>
            <div
              v-if="!isLoading && badTransactionsResponse.type !== null"
              id="notification-message"
              class="message-info has-text-centered"
              :class="(badTransactionsResponse.type === 'error' ? 'is-warning' : 'is-info')">
              <i class="fas fa-exclamation-circle is-pulled-left" />
              <div class="message-text">
                <span>{{ badTransactionsResponse.message }}</span>
              </div>
            </div>
            <div v-else>
              <h5 v-if="confirmableTransactionsLen" class="has-text-centered mb-3">
                {{ $t('transactions.thereAre') }} <span class="has-text-weight-bold">{{ confirmableTransactionsLen }}
                  {{ $t(`generalWords.transaction${confirmableTransactionsLen > 1 ? 's' : '' }`) }}</span>
                {{ $t('transactions.toValidate') }}, <span class="has-text-weight-bold">
                  {{
                    filteredTransactionsLen > 0
                      ? `mostrando ${filteredTransactionsLen}`
                      : $t('transactions.noCoincidences')
                  }}
                </span>
              </h5>
              <b-table
                hoverable
                :data="filteredTransactions">
                <b-table-column v-slot="transaction" field="amount" :label="$t('generalWords.Amount')">
                  <span :id="getTransactionId(transaction.row)" class="tag is-primary is-light is-size-6">{{ formatNumberByCountry(transaction.row.amount) }}</span>
                </b-table-column>
                <b-table-column v-slot="transaction" field="createdAt" :label="$t('generalWords.Antiquity')">
                  {{ daysDiffFromToday(transaction.row.createdAt) }} {{ $t('generalWords.days') }}
                </b-table-column>
                <b-table-column v-slot="transaction" field="clientName" :label="$t('generalWords.Name')">
                  {{ transaction.row.client.name }} {{ transaction.row.client.lastName }}
                </b-table-column>
                <b-table-column v-slot="transaction" field="clientIdNumber" :label="$t('generalWords.idNumber')">
                  {{ formatRut(transaction.row.clientIdNumber) }}
                </b-table-column>
                <b-table-column v-slot="transaction" field="createdAt" :label="$t('generalWords.Date')">
                  {{ formatDate(transaction.row.createdAt) }}
                </b-table-column>
                <b-table-column v-slot="transaction" field="button" label="">
                  <button
                    v-if="transaction.row.provider !== 'SERVIPAG_BUTTON'"
                    :id="`${getTransactionId(transaction.row)}-review`"
                    :disabled="!checkUserFeature('element.transaction-approvals.button.register')"
                    class="button is-button-primary is-rounded"
                    @click="openSingleProcess(transaction.row._id)">
                    {{ $t('generalWords.Check') }}
                  </button>
                </b-table-column>
              </b-table>
            </div>
          </div>
          <div v-if="singleProcess.isOpen">
            <SingleProcess
              :bank-entries="bankEntries"
              :confirmable-transactions="confirmableTransactions"
              :current-transaction="singleProcess.currentTransaction"
              :current-transaction-id="singleProcess.currentTransactionId"
              :current-transaction-index="singleProcess.currentTransactionIndex"
              :debt-date-toggle="debtDateToggle"
              :payment-providers="paymentProviders"
              :provider-dictionary="providerDictionary"
              @is-single-process-open="singleProcess.isOpen = $event"
              @load-transactions="loadTransactions()"
              @update-transaction="updateTransaction" />
          </div>
          <div v-if="bulkProcess.isOpen">
            <h3 class="modal-title">Validación masiva</h3>
            <section>
              <h5 class="is-size-5">Rendición Servipag</h5>
              <div class="control">
                <textarea
                  v-model="bulkProcess.bulkInputTransactionText"
                  class="textarea"
                  rows="2"
                  @drop.prevent="readServipagFile"
                  @dragover.prevent />
              </div>
              <BankStatementDetails
                v-model="bulkProcess.selectedBankEntry"
                id-prefix="servipag"
                :bank-entries="bankEntries"
                type="radio"
                :default-search="['78.053.790-6']"
                :totals="false" />
              <h5 class="is-size-5">Comentario</h5>
              <div class="control">
                <input
                  id="text-input-comment"
                  v-model="bulkProcess.comment"
                  type="text"
                  class="input is-small">
              </div>
              <p v-if="bulkProcess.errorText" class="help is-danger"> {{ bulkProcess.errorText }} </p>
              <div class="transaction-buttons has-text-right">
                <button
                  class="button is-button-secondary is-rounded mr-5"
                  @click="bulkProcess.isOpen=false">
                  {{ $t('generalWords.GoBack') }}
                </button>
                <b-button
                  id="bulk-process-button"
                  rounded
                  :loading="bulkProcess.loading"
                  class="button is-button-primary is-pulled-right"
                  @click="processBulkTransactionIds">
                  Procesar
                </b-button>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
    <b-modal v-model="filters.isModalVisible" :width="400">
      <div class="modal-card-body has-text-centered">
        <div>
          <a id="close-filter-modal" class="close-modal" @click="filters.isModalVisible=false">
            <img src="@/ui/assets/img/plus-button.svg">
          </a>
        </div>
        <h3 class="modal-title">{{ $t('generalWords.Filter') }} </h3>

        <b-field horizontal :label="$t('generalWords.Month')" custom-class="has-text-weight-normal">
          <b-select
            id="month"
            v-model="filters.month"
            expanded
            name="month"
            size="is-small"
            rounded>
            <option value="" selected>{{ $t('transactions.filter.selectMonth') }}</option>
            <option
              v-for="key in Object.keys(monthsToFilter).sort()"
              :key="key"
              :value="key">
              {{ monthsToFilter[key] }}
            </option>
          </b-select>
        </b-field>
        <b-field horizontal :label="$t('generalWords.Operation')" custom-class="has-text-weight-normal">
          <b-select
            id="typeOperationToFilter"
            v-model="filters.typeOperation"
            expanded
            name="typeOperationToFilter"
            size="is-small"
            rounded>
            <option value="" selected>{{ $t('transactions.filter.selectOperation') }}</option>
            <option
              v-for="key in Object.keys(typeOperationToFilter).sort()"
              :key="key"
              :value="key">
              {{ typeOperationToFilter[key] }}
            </option>
          </b-select>
        </b-field>
        <b-field horizontal label="Tipo" custom-class="has-text-weight-normal">
          <div class="has-text-left">
            <label
              v-for="provider in providersList"
              :key="`label-provider-${provider}`"
              class="checkbox is-block">
              <input
                :id="`checkbox-provider-${provider}`"
                v-model="filters.type"
                type="checkbox"
                :value="provider">
              {{ providerDictionary[provider] }}
            </label>
          </div>
        </b-field>
        <b-field horizontal :label="$t('generalWords.Search')" custom-class="has-text-weight-normal">
          <b-input
            id="search-input"
            v-model="filters.searchInputVal"
            type="search"
            expanded
            icon="search"
            icon-clickable
            :disabled="!confirmableTransactionsLen"
            :placeholder="`id de ${$t('generalWords.transaction')}, ${$t('generalWords.idNumber')}, id CMF`"
            size="is-small"
            rounded />
        </b-field>
        <div id="filter-info" class="mt-3 mb-3 has-text-centered">
          {{ $t('generalWords.Filtering') }}
          <span class="has-text-weight-bold">{{ filteredTransactionsLen }}</span>
          de <span class="has-text-weight-bold">{{ confirmableTransactionsLen }}</span> {{ $t('generalWords.transactions') }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import BankStatementDetails from '@/ui/components/internal/transaction-approval/BankStatementDetails.vue';
import SingleProcess from '@/ui/components/internal/transaction-approval/SingleProcess.vue';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';
import {
  getTransactionsProviders,
  getConfirmableTransactions,
  confirmGroupOfTransactions
} from '@/application/services/api-clients/transactions';
import {
  dateAdd,
  getDaysDifferenceBetweenDays,
  dateToFormatedString
} from '@/infrastructure/date';
import { formatNumber, formatNumberByCountry } from '@/infrastructure/format';
import userFeatures from '@/ui/mixins/user-features';
import notification from '@/ui/mixins/notification';
import { getUnassignedBankEntries } from '@/application/services/api-clients/bank-entries';

export default {
  name: 'TransactionsValidation',
  components: { SingleProcess, BankStatementDetails },
  mixins: [userFeatures, notification],
  data() {
    return {
      debtDateToggle: false,
      confirmableTransactions: [],
      bankEntries: [],
      filters: {
        isModalVisible: false,
        type: [
          'WIRE_TRANSFER',
          'CASH_DEPOSIT',
          ...['br'].includes(this.$i18n.locale) ? [undefined] : []
        ],
        searchInputVal: '',
        typeOperation: '',
        month: ''
      },
      providersList: [undefined],
      paymentProviders: [],
      isLoading: true,
      badTransactionsResponse: {
        type: null,
        message: null
      },
      providerDictionary: {
        undefined: {
          br: 'Pendente',
          cl: 'Pendiente',
          pe: 'Pendiente'
        }[this.$i18n.locale]
      },
      errorDictionary: {
        br: {
          noPendingTransactions: 'Nenhuma transação pendente encontrada',
          errorGettingTransactions: 'Ocorreu um erro ao obter as transações',
          errorRegisteringTransaction: 'Ocorreu um erro ao tentar processar a transação',
          noTransactionsText: 'Não consigo encontrar as transações que você indica como pendentes. Copie novamente a versão',
          badAmountError: 'O valor deve ser igual a toda a prestação',
          serverError: 'Ocorreu um erro ao tentar se comunicar com o servido',
          uploadFile: 'Ocorreu um erro ao carregar o arquivo'
        },
        cl: {
          noPendingTransactions: 'No se encontraron transacciones pendientes',
          errorGettingTransactions: 'Ha ocurrido un error al obtener las transacciones',
          errorRegisteringTransaction: 'Ha ocurrido un error al tratar de procesar la transacción',
          noTransactionsText: 'No encuentro como pendientes las transacciones que me indicas. Por favor vuelve a copiar la rendición',
          badAmountError: 'El monto debe ser igual a la totalidad de la rendición',
          serverError: 'Ocurrió un error al tratar de comunicarse con el servidor',
          uploadFile: 'Hubo un error al subir el archivo'
        },
        pe: {
          noPendingTransactions: 'No se encontraron transacciones pendientes',
          errorGettingTransactions: 'Ha ocurrido un error al obtener las transacciones',
          errorRegisteringTransaction: 'Ha ocurrido un error al tratar de procesar la transacción',
          noTransactionsText: 'No encuentro como pendientes las transacciones que me indicas. Por favor vuelve a copiar la rendición',
          badAmountError: 'El monto debe ser igual a la totalidad de la rendición',
          serverError: 'Ocurrió un error al tratar de comunicarse con el servidor',
          uploadFile: 'Hubo un error al subir el archivo'
        }
      }[this.$i18n.locale],
      typeOperationToFilter: {
        MORTGAGE: this.$t('generalWords.Mortgage'),
        COMPLEMENTARY: this.$t('generalWords.Complementary'),
        PARKING: this.$t('generalWords.Parking'),
        SME: 'SME'
      },
      singleProcess: {
        currentTransaction: {},
        currentTransactionId: '',
        currentTransactionIndex: -1,
        isOpen: false
      },
      bulkProcess: {
        selectedBankEntry: { totalAmount: 0, bankEntriesIds: [], bankEntriesEntities: [] },
        comment: ' ',
        loading: false,
        errorText: '',
        isOpen: false,
        bulkInputTransactionText: ''
      }
    };
  },
  computed: {
    confirmableTransactionsIds() {
      return this.confirmableTransactions.map(x => x._id);
    },
    filteredTransactions() {
      const f = this.filters;
      return this.confirmableTransactions.filter(transaction => {
        if (f.type.length > 0 && !f.type.includes(transaction.provider)) {
          return false;
        }
        if (f.month !== '') {
          const currentMonth = this.formatDate(transaction.createdAt, 'MM/YYYY');
          if (currentMonth !== f.month) {
            return false;
          }
        }
        if (f.searchInputVal !== '') {
          const searchValue = this.parseSearchString(f.searchInputVal);
          const isNotIncluded = !(
            this.parseSearchString(transaction._id).includes(searchValue)
            || this.parseSearchString(transaction.clientIdNumber).includes(searchValue)
            || transaction.selectedPayments.map(op => this.parseSearchString(op.companyOperationId)).some(x => x.includes(searchValue))
          );
          if (isNotIncluded) {
            return false;
          }
        }
        if (f.typeOperation !== '') {
          return !!transaction.selectedPayments.find(operation => operation.type === f.typeOperation);
        }
        return true;
      });
    },
    confirmableTransactionsLen() {
      return this.confirmableTransactions.length;
    },
    filteredTransactionsLen() {
      return this.filteredTransactions.length;
    },
    bulkProcessSelectedBankEntry() {
      return this.bulkProcess.selectedBankEntry;
    },
    monthsToFilter() {
      return this.confirmableTransactions.reduce((acc, confirmableTransaction) => {
        const currentMonth = this.formatDate(confirmableTransaction.createdAt, 'MM/YYYY');
        if (!acc[currentMonth]) {
          acc[currentMonth] = currentMonth;
        }
        return acc;
      }, {});
    }
  },
  watch: {
    bulkProcessSelectedBankEntry() {
      this.bulkProcess.errorText = '';
    }
  },
  async mounted() {
    this.isLoading = true;
    if (['cl', 'br'].includes(this.$i18n.locale)) {
      const [debtDateToggle, paymentProviders] = await Promise.all([
        getFeatureToggleByName('update-transaction-debt-date'),
        getTransactionsProviders(),
        this.loadTransactions(),
        this.loadBankEntries()
      ]);
      this.debtDateToggle = debtDateToggle.isActive;
      this.paymentProviders = paymentProviders;
      this.paymentProviders.forEach(x => {
        this.providersList.unshift(x.slug);
        this.providerDictionary[x.slug] = x.name;
      });
    }
    this.isLoading = false;
  },
  methods: {
    parseSearchString: string => string.toString().toUpperCase().replace(/\./g, ''),
    daysDiffFromToday: date => getDaysDifferenceBetweenDays(date, new Date()),
    formatDate: (date, format = 'DD/MM/YYYY HH:mm', inputFormat = '') => dateToFormatedString(date, format, inputFormat),
    formatNumberByCountry(number) {
      const fractionDigits = {
        br: 2,
        cl: 0,
        pe: 2
      }[this.$i18n.locale];
      return formatNumberByCountry({ number, countryIn: this.$i18n.locale, usePrefix: true, fractionDigits, currencyType: 'secondary' });
    },
    formatRut(rut) {
      const rutParts = rut.split('-');
      return `${formatNumber({ number: Number(rutParts[0]) })}-${rutParts[1]}`;
    },
    showTransactionsValidation() {
      this.singleProcess.isOpen = false;
      this.bulkProcess.isOpen = false;
    },
    getTransactionId(transaction) {
      return transaction.selectedPayments.reduce((acc, operation) => {
        let accumulator = `${acc}${acc === '' ? '' : '|'}`;
        accumulator += `${operation.companyOperationId}`;
        operation.payments.forEach(payment => {
          accumulator += `-${payment.paymentNumber}`;
        });
        return accumulator;
      }, '');
    },
    async loadTransactions() {
      this.filters.searchInputVal = '';
      this.filters.typeOperation = '';
      try {
        this.confirmableTransactions = await getConfirmableTransactions();
      } catch (_error) {
        this.badTransactionsResponse.message = this.errorDictionary.errorGettingTransactions;
        this.badTransactionsResponse.type = 'error';
        return;
      }
      if (!this.confirmableTransactions.length) {
        this.badTransactionsResponse.message = this.errorDictionary.noPendingTransactions;
        this.badTransactionsResponse.type = 'no-pendings';
      }
    },
    async loadBankEntries() {
      const dateFrom = '2020-01-01';
      const dateTo = dateToFormatedString(dateAdd(new Date(), 1, 'month'), 'YYYY-MM-DD', '');
      try {
        this.bankEntries = await getUnassignedBankEntries('', dateFrom, dateTo, 'INCOME');
      } catch (_error) {
        this.alert({ message: 'Ocurrió un error al conectarnos con el servidor' });
      }
    },
    updateTransaction({ transaction, index }) {
      const updatedTransaction = transaction;
      const { client, clientIdNumber, trustedIdNumbers } = this.confirmableTransactions[index];
      updatedTransaction.clientIdNumber = clientIdNumber;
      updatedTransaction.client = client;
      updatedTransaction.trustedIdNumbers = trustedIdNumbers;
      this.confirmableTransactions[index] = updatedTransaction;
      this.singleProcess.currentTransactionId = updatedTransaction._id;
      this.singleProcess.currentTransaction = this.confirmableTransactions.find(ct => ct._id === updatedTransaction._id);
      this.singleProcess.currentTransactionIndex = this.confirmableTransactions.findIndex(ct => ct._id === updatedTransaction._id);
    },
    openBulkProcess() {
      this.bulkProcess.selectedBankEntry = { totalAmount: 0, bankEntriesIds: [], bankEntriesEntities: [] };
      this.bulkProcess.comment = '';
      this.bulkProcess.loading = false;
      this.bulkProcess.errorText = '';
      this.bulkProcess.bulkInputTransactionText = '';
      this.bulkProcess.isOpen = true;
    },
    async processBulkTransactionIds() {
      this.bulkProcess.loading = true;
      this.bulkProcess.errorText = '';
      const lines = this.bulkProcess.bulkInputTransactionText.split(/\r?\n/).filter(x => x.length > 0);
      const transactionsIds = lines.map(line => line.substring(12, (24 + 12)));
      const allTransactionsExist = transactionsIds.every(x => this.confirmableTransactionsIds.includes(x));
      if (!allTransactionsExist) {
        this.bulkProcess.errorText = this.errorDictionary.noTransactionsText;
        this.bulkProcess.loading = false;
        return;
      }
      const totalAmount = this.confirmableTransactions.reduce((total, transaction) => {
        if (transactionsIds.includes(transaction._id)) {
          return total + transaction.amount;
        }
        return total;
      }, 0);
      const selectedBankEntryAmount = this.bulkProcess.selectedBankEntry.totalAmount;
      if (selectedBankEntryAmount !== totalAmount) {
        this.bulkProcess.errorText = this.errorDictionary.badAmountError;
        this.bulkProcess.loading = false;
        return;
      }
      try {
        await confirmGroupOfTransactions(
          transactionsIds,
          selectedBankEntryAmount,
          this.bulkProcess.comment,
          this.bulkProcess.selectedBankEntry.bankEntriesIds[0]
        );
      } catch (_error) {
        this.bulkProcess.errorText = this.errorDictionary.serverError;
      }
      this.bulkProcess.loading = false;
      this.bulkProcess.isOpen = false;
      this.isLoading = true;
      this.loadTransactions();
      this.isLoading = false;
    },
    openSingleProcess(transactionId) {
      this.singleProcess.currentTransactionId = transactionId;
      this.singleProcess.currentTransaction = this.confirmableTransactions.find((t, i) => {
        const found = t._id === transactionId;
        if (found) {
          this.singleProcess.currentTransactionIndex = i;
        }
        return found;
      });
      this.singleProcess.isOpen = true;
    },
    readServipagFile(event) {
      const droppedFiles = event.dataTransfer.files;
      if (!droppedFiles) {
        return;
      }
      const [file] = droppedFiles;
      const reader = new FileReader();
      reader.onload = e => {
        const textFromReadedFile = e.target.result;
        this.bulkProcess.bulkInputTransactionText = textFromReadedFile;
      };
      reader.readAsText(file);
    }
  }
};
</script>

<style scoped>
.select-content{
  min-height: 150px;
}
  #search-input {
    margin-top: 1rem;
  }
  #validation-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    align-content: space-between;
    flex-flow: wrap;
  }
  #validation-buttons p {
    margin-bottom: 10px;
  }
  .transaction-buttons {
    margin-top: 15px;
  }
  .spinner-content{
    height: 150px;
  }
</style>
